import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Public as PublicIcon,
  LocationOn as LocationOnIcon,
  Search as SearchIcon,
  Tv as TvIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { playlistsAPI, channelsAPI } from '../services/api';

// Компонент заголовка с поиском (без dropdown)
const SearchTableHeader = ({ label, value, onChange, placeholder }) => {
  return (
    <TableCell sx={{ p: 1, minWidth: 120 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          {label}
        </Typography>
        <TextField
          size="small"
          placeholder={placeholder || 'Поиск...'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          InputProps={{
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onChange('')}
                  sx={{ p: 0.25 }}
                >
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.75rem',
            },
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.5
            }
          }}
        />
      </Box>
    </TableCell>
  );
};

// Компонент заголовка с селектором статуса
const StatusFilterHeader = ({ label, value, onChange }) => {
  return (
    <TableCell sx={{ p: 1, minWidth: 120 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          {label}
        </Typography>
        <TextField
          select
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          SelectProps={{
            native: true,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.75rem',
            },
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.5
            }
          }}
        >
          <option value="all">Все</option>
          <option value="active">Активен</option>
          <option value="inactive">Не активен</option>
        </TextField>
      </Box>
    </TableCell>
  );
};

// Компонент для сортируемой строки таблицы
const SortableTableRow = ({ channel, index, children, isDragDropEnabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: channel.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: isDragDropEnabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
      {...(isDragDropEnabled ? attributes : {})}
      {...(isDragDropEnabled ? listeners : {})}
    >
      {children}
    </TableRow>
  );
};

// Компонент карточки канала для мобильных устройств
const SortableChannelCard = ({ 
  channel, 
  index, 
  onEdit, 
  onDelete, 
  onMoveUp, 
  onMoveDown, 
  isDragDropEnabled, 
  canMoveUp, 
  canMoveDown, 
  renderGeoRestrictions,
  onShowDragDropWarning 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: channel.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      sx={{ 
        mb: 1, 
        border: isDragging ? '2px dashed #1976d2' : '1px solid #e0e0e0',
        backgroundColor: isDragging ? '#f5f5f5' : 'white',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Первая строка: основная информация */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 1,
          width: '100%'
        }}>
          {/* Номер */}
          <Typography variant="body2" sx={{ 
            color: 'text.secondary', 
            fontSize: '0.8rem', 
            fontWeight: 600,
            minWidth: '24px',
            textAlign: 'center'
          }}>
            #{index + 1}
          </Typography>

          {/* Иконка */}
          <Avatar
            src={channel.icon}
            alt={channel.name}
            sx={{ width: 36, height: 36 }}
          >
            {channel.name?.charAt(0)}
          </Avatar>

          {/* Название и статус */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ 
              fontSize: '0.9rem', 
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mb: 0.25
            }}>
              {channel.name || 'Без названия'}
            </Typography>
            <Typography variant="caption" sx={{ 
              color: 'text.secondary',
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}>
              {channel.url ? (() => {
                try {
                  return new URL(channel.url).hostname;
                } catch {
                  return channel.url.length > 25 ? channel.url.substring(0, 25) + '...' : channel.url;
                }
              })() : 'URL не указан'}
            </Typography>
          </Box>

          {/* Статус */}
          <Chip
            label={channel.is_active ? 'Активен' : 'Отключен'}
            color={channel.is_active ? 'success' : 'error'}
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
        </Box>

        {/* Вторая строка: гео-ограничения и управление */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%',
          minHeight: '44px'
        }}>
          {/* Гео-ограничения */}
          <Box sx={{ flex: 1, mr: 1 }}>
            {renderGeoRestrictions(channel)}
          </Box>

          {/* Кнопки управления (touch-friendly) */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {/* Drag handle ИЛИ стрелочки */}
            {isDragDropEnabled ? (
              <Box 
                {...attributes} 
                {...listeners}
                sx={{ 
                  cursor: 'grab', 
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: 'action.hover',
                  '&:active': { cursor: 'grabbing' },
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: '44px',
                  minHeight: '44px',
                  justifyContent: 'center'
                }}
              >
                <DragIndicatorIcon sx={{ fontSize: '18px' }} />
              </Box>
            ) : (
              <>
                <IconButton
                  size="medium"
                  onClick={() => onMoveUp(channel.id)}
                  disabled={!canMoveUp}
                  title="Переместить вверх"
                  sx={{ 
                    minWidth: '44px', 
                    minHeight: '44px',
                    backgroundColor: 'action.hover',
                    '&:hover': { backgroundColor: 'action.selected' }
                  }}
                >
                  <ArrowUpwardIcon sx={{ fontSize: '18px' }} />
                </IconButton>
                <IconButton
                  size="medium"
                  onClick={() => onMoveDown(channel.id)}
                  disabled={!canMoveDown}
                  title="Переместить вниз"
                  sx={{ 
                    minWidth: '44px', 
                    minHeight: '44px',
                    backgroundColor: 'action.hover',
                    '&:hover': { backgroundColor: 'action.selected' }
                  }}
                >
                  <ArrowDownwardIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              </>
            )}
            
            {/* Основные действия */}
            <IconButton
              size="medium"
              onClick={() => {
                if (isDragDropEnabled) {
                  onShowDragDropWarning && onShowDragDropWarning();
                  return;
                }
                onEdit(channel);
              }}
              title="Редактировать"
              sx={{ 
                minWidth: '44px', 
                minHeight: '44px',
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                '&:hover': { backgroundColor: 'primary.main' }
              }}
            >
              <EditIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            <IconButton
              size="medium"
              onClick={() => {
                if (isDragDropEnabled) {
                  onShowDragDropWarning && onShowDragDropWarning();
                  return;
                }
                onDelete(channel);
              }}
              title="Удалить"
              sx={{ 
                minWidth: '44px', 
                minHeight: '44px',
                backgroundColor: 'error.light',
                color: 'error.contrastText',
                '&:hover': { backgroundColor: 'error.main' }
              }}
            >
              <DeleteIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const PlaylistChannelsPage = () => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  
  // Мобильная адаптивность
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // xs, sm = мобильные
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // md = планшеты
  
  const [playlist, setPlaylist] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);
  
  // Состояния для фильтрации
  const [nameSearch, setNameSearch] = useState('');
  const [urlSearch, setUrlSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  
  // Состояния для добавления существующих каналов
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [allChannels, setAllChannels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Состояния для drag-and-drop режима
  const [isDragDropEnabled, setIsDragDropEnabled] = useState(() => {
    // Восстанавливаем состояние из localStorage
    const saved = localStorage.getItem('dragDropEnabled');
    return saved === 'true';
  });
  const [showDragDropWarning, setShowDragDropWarning] = useState(false);

  // Сохраняем состояние drag-and-drop в localStorage
  const handleDragDropToggle = (checked) => {
    console.log('Drag-and-drop toggle:', checked);
    setIsDragDropEnabled(checked);
    localStorage.setItem('dragDropEnabled', checked.toString());
  };

  // Сенсоры для drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadPlaylistAndChannels();
    loadAllChannels();
  }, [playlistId]);

  const loadAllChannels = async () => {
    try {
      const response = await channelsAPI.getAll();
      setAllChannels(response.data);
    } catch (error) {
      console.error('Ошибка загрузки списка каналов:', error);
    }
  };

  const handleAddExistingChannel = async (channel) => {
    try {
      const maxSortOrder = channels.length > 0 ? Math.max(...channels.map(c => c.sort_order)) : 0;
      await playlistsAPI.addChannel(playlistId, channel.id, maxSortOrder + 1);
      setSnackbar({
        open: true,
        message: 'Канал добавлен в плейлист',
        severity: 'success'
      });
      setOpenAddDialog(false);
      loadPlaylistAndChannels();
    } catch (error) {
      console.error('Ошибка добавления канала:', error);
      setSnackbar({
        open: true,
        message: 'Ошибка добавления канала',
        severity: 'error'
      });
    }
  };

  const loadPlaylistAndChannels = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Loading playlist and channels for playlistId:', playlistId);
      
      console.log('Fetching playlist...');
      const playlistResponse = await playlistsAPI.getById(playlistId);
      console.log('Playlist response:', playlistResponse);
      setPlaylist(playlistResponse.data);
      
      console.log('Fetching channels...');
      const channelsResponse = await playlistsAPI.getChannels(playlistId);
      console.log('Channels response:', channelsResponse);
      setChannels(channelsResponse.data);
      
      console.log('Data loaded successfully');
    } catch (err) {
      console.error('Error loading playlist and channels:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: err.config?.url
      });
      setError('Ошибка загрузки плейлиста и каналов');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveChannel = async (channelId) => {
    try {
      await playlistsAPI.removeChannel(playlistId, channelId);
      await loadPlaylistAndChannels();
      setDeleteDialogOpen(false);
      setChannelToDelete(null);
    } catch (err) {
      console.error('Error removing channel:', err);
      setError('Ошибка удаления канала из плейлиста');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    console.log('Drag end event:', { active: active?.id, over: over?.id });

    if (active.id !== over?.id) {
      const oldIndex = channels.findIndex((channel) => channel.id === active.id);
      const newIndex = channels.findIndex((channel) => channel.id === over.id);
      console.log('Drag indices:', { oldIndex, newIndex, activeId: active.id, overId: over.id });

      if (oldIndex !== -1 && newIndex !== -1) {
        // Обновляем локальное состояние для мгновенной обратной связи
        const newChannels = arrayMove(channels, oldIndex, newIndex);
        setChannels(newChannels);
        console.log('Local state updated, sending API request...');

        try {
          // Используем move вместо swap для правильного перемещения
          const response = await playlistsAPI.reorderChannels(playlistId, {
            channel_id: active.id,
            new_position: newIndex
          });
          console.log('API response:', response);
          
          // Перезагружаем данные с сервера для получения обновленных sort_order
          await loadPlaylistAndChannels();
          console.log('Data reloaded successfully');
        } catch (err) {
          console.error('Error reordering channels:', err);
          setError('Ошибка изменения порядка каналов');
          // Восстанавливаем исходный порядок при ошибке
          await loadPlaylistAndChannels();
        }
      }
    }
  };

  // Функция для проверки drag-and-drop режима для кнопок редактирования/удаления
  const checkDragDropModeForEditDelete = () => {
    if (isDragDropEnabled) {
      setShowDragDropWarning(true);
      return true;
    }
    return false;
  };

  // Функция для проверки drag-and-drop режима для стрелочек сортировки
  const checkDragDropModeForArrows = () => {
    if (isDragDropEnabled) {
      setShowDragDropWarning(true);
      return true;
    }
    return false;
  };

  const handleMoveChannel = async (channelId, direction) => {
    console.log('Arrow button clicked:', { channelId, direction, isDragDropEnabled });
    
    // Проверяем, включен ли drag-and-drop режим
    if (checkDragDropModeForArrows()) {
      console.log('Arrow blocked: drag-and-drop mode is enabled');
      return; // Выходим, если режим включен и показано уведомление
    }

    try {
      const currentIndex = channels.findIndex(ch => ch.id === channelId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      console.log('Arrow move:', { currentIndex, targetIndex, channelsLength: channels.length });
      
      if (targetIndex >= 0 && targetIndex < channels.length) {
        const currentChannel = channels[currentIndex];
        const targetChannel = channels[targetIndex];
        console.log('Swapping channels:', { current: currentChannel.id, target: targetChannel.id });
        
        // Используем тот же API что и drag-and-drop (swap двух каналов)
        const response = await playlistsAPI.reorderChannels(playlistId, {
          channel1_id: currentChannel.id,
          channel2_id: targetChannel.id
        });
        console.log('Arrow API response:', response);
        
        // Перезагружаем данные с сервера
        await loadPlaylistAndChannels();
        console.log('Arrow: Data reloaded successfully');
      } else {
        console.log('Arrow move blocked: target index out of bounds');
      }
    } catch (err) {
      console.error('Error moving channel:', err);
      setError('Ошибка изменения порядка канала');
    }
  };

  const renderGeoRestrictions = (geoRestrictions) => {
    if (!geoRestrictions || geoRestrictions.type === 'world') {
      return (
        <Chip
          icon={<PublicIcon />}
          label="Весь мир"
          size="small"
          color="success"
          variant="outlined"
        />
      );
    }
    
    if (geoRestrictions.type === 'countries' && geoRestrictions.countries) {
      return (
        <Chip
          icon={<LocationOnIcon />}
          label={`${geoRestrictions.countries.length} стран`}
          size="small"
          color="warning"
          variant="outlined"
        />
      );
    }
    
    return null;
  };

  // Фильтрация каналов
  const filteredChannels = channels.filter(channel => {
    // Фильтр по названию
    if (nameSearch && !channel.name?.toLowerCase().includes(nameSearch.toLowerCase())) {
      return false;
    }
    // Фильтр по URL
    if (urlSearch && !channel.url?.toLowerCase().includes(urlSearch.toLowerCase())) {
      return false;
    }
    // Фильтр по статусу
    if (statusFilter === 'active' && !channel.is_active) {
      return false;
    }
    if (statusFilter === 'inactive' && channel.is_active) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Заголовок с кнопкой назад и кнопкой добавления */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/playlists')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {playlist?.name || 'Плейлист'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 2 }}>
            Управление каналами
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={isDragDropEnabled}
                onChange={(e) => handleDragDropToggle(e.target.checked)}
                color="primary"
              />
            }
            label="Режим перетаскивания"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
          >
            Добавить канал
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Адаптивное отображение каналов: карточки на мобильных, таблица на десктопе */}
      {isMobile ? (
        /* МОБИЛЬНАЯ ВЕРСИЯ - КАРТОЧКИ */
        <DndContext 
          sensors={sensors} 
          collisionDetection={closestCenter} 
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={channels.map(channel => channel.id)} 
            strategy={verticalListSortingStrategy}
          >
            <Box sx={{ 
              width: '100%', 
              maxWidth: '100vw', 
              overflow: 'hidden',
              px: 0
            }}>
              {filteredChannels.map((channel, index) => (
                <SortableChannelCard
                  key={channel.id}
                  channel={channel}
                  index={index}
                  onEdit={(channel) => navigate(`/playlists/${playlistId}/channels/${channel.id}`)}
                  onDelete={(channel) => {
                    setChannelToDelete(channel);
                    setDeleteDialogOpen(true);
                  }}
                  onMoveUp={(channelId) => handleMoveChannel(channelId, 'up')}
                  onMoveDown={(channelId) => handleMoveChannel(channelId, 'down')}
                  isDragDropEnabled={isDragDropEnabled}
                  canMoveUp={index > 0}
                  canMoveDown={index < channels.length - 1}
                  renderGeoRestrictions={renderGeoRestrictions}
                  onShowDragDropWarning={() => setShowDragDropWarning(true)}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      ) : (
        /* ДЕСКТОПНАЯ ВЕРСИЯ - ТАБЛИЦА */
        isDragDropEnabled ? (
          <DndContext 
            sensors={sensors} 
            collisionDetection={closestCenter} 
            onDragEnd={handleDragEnd}
          >
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ p: 1, width: 100 }}>Порядок</TableCell>
                    <SearchTableHeader 
                      label="Канал" 
                      value={nameSearch} 
                      onChange={setNameSearch}
                      placeholder="Поиск по названию..."
                    />
                    <SearchTableHeader 
                      label="URL" 
                      value={urlSearch} 
                      onChange={setUrlSearch}
                      placeholder="Поиск по URL..."
                    />
                    <StatusFilterHeader 
                      label="Статус" 
                      value={statusFilter} 
                      onChange={setStatusFilter}
                    />
                    <TableCell align="right" sx={{ p: 1 }}>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <SortableContext 
                  items={filteredChannels.map(channel => channel.id)} 
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {filteredChannels.map((channel, index) => (
                      <SortableTableRow key={channel.id} channel={channel} index={index} isDragDropEnabled={isDragDropEnabled}>
                        <TableCell sx={{ p: 1 }}>
                          <Box display="flex" alignItems="center">
                            {isDragDropEnabled && <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1.2rem' }} />}
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{index + 1}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ p: 1 }}>
                          <Box display="flex" alignItems="center">
                            <Avatar
                              src={channel.icon}
                              alt={channel.name}
                              sx={{ width: 28, height: 28, mr: 1.5 }}
                            >
                              {channel.name?.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{channel.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ p: 1 }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontSize: '0.875rem' }}>
                            {channel.url}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ p: 1 }}>
                          <Chip
                            label={channel.is_active ? 'Активен' : 'Отключен'}
                            color={channel.is_active ? 'success' : 'error'}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ p: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Перетащите для изменения порядка
                          </Typography>
                        </TableCell>
                      </SortableTableRow>
                    ))}
                  </TableBody>
                </SortableContext>
              </Table>
            </TableContainer>
          </DndContext>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ p: 1, width: 100 }}>Порядок</TableCell>
                  <SearchTableHeader 
                    label="Канал" 
                    value={nameSearch} 
                    onChange={setNameSearch}
                    placeholder="Поиск по названию..."
                  />
                  <SearchTableHeader 
                    label="URL" 
                    value={urlSearch} 
                    onChange={setUrlSearch}
                    placeholder="Поиск по URL..."
                  />
                  <StatusFilterHeader 
                    label="Статус" 
                    value={statusFilter} 
                    onChange={setStatusFilter}
                  />
                  <TableCell align="right" sx={{ p: 1 }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredChannels.map((channel, index) => (
                  <SortableTableRow key={channel.id} channel={channel} index={index} isDragDropEnabled={isDragDropEnabled}>
                    <TableCell sx={{ p: 1 }}>
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{index + 1}</Typography>
                        <Box ml={1}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveChannel(channel.id, 'up');
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            disabled={index === 0}
                            sx={{ p: 0.5 }}
                          >
                            <ArrowUpwardIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveChannel(channel.id, 'down');
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            disabled={index === filteredChannels.length - 1}
                            sx={{ p: 0.5 }}
                          >
                            <ArrowDownwardIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Box display="flex" alignItems="center">
                        <Avatar
                          src={channel.icon}
                          alt={channel.name}
                          sx={{ width: 28, height: 28, mr: 1.5 }}
                        >
                          {channel.name?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{channel.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontSize: '0.875rem' }}>
                        {channel.url}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Chip
                        label={channel.is_active ? 'Активен' : 'Отключен'}
                        color={channel.is_active ? 'success' : 'error'}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ p: 1 }}>
                      <Tooltip title="Редактировать канал">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (checkDragDropModeForEditDelete()) return;
                            navigate(`/playlists/${playlistId}/channels/${channel.id}`);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Удалить из плейлиста">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (checkDragDropModeForEditDelete()) return;
                            setChannelToDelete(channel);
                            setDeleteDialogOpen(true);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </SortableTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {channels.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            В плейлисте пока нет каналов
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/playlists/${playlistId}/channels/new`)}
            sx={{ mt: 2 }}
          >
            Добавить первый канал
          </Button>
        </Box>
      )}



      {/* Диалог добавления существующего канала */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => setOpenAddDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Добавить канал в плейлист
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              placeholder="Поиск по названию или URL..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {allChannels
              .filter(channel => 
                !channels.some(pc => pc.id === channel.id) &&
                (channel.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 channel.url?.toLowerCase().includes(searchTerm.toLowerCase()))
              )
              .map((channel) => (
                <ListItem key={channel.id} disablePadding>
                  <ListItemButton onClick={() => handleAddExistingChannel(channel)}>
                    <Avatar
                      src={channel.icon}
                      alt={channel.name}
                      sx={{ width: 40, height: 40, mr: 2 }}
                    >
                      <TvIcon />
                    </Avatar>
                    <ListItemText
                      primary={channel.name || 'Без названия'}
                      secondary={channel.url || 'URL не указан'}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            {allChannels.filter(channel => 
              !channels.some(pc => pc.id === channel.id) &&
              (channel.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               channel.url?.toLowerCase().includes(searchTerm.toLowerCase()))
            ).length === 0 && (
              <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                {searchTerm ? 'Каналы не найдены' : 'Все каналы уже добавлены в плейлист'}
              </Typography>
            )}
          </List>
          <Divider sx={{ my: 2 }} />
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setOpenAddDialog(false);
              navigate(`/playlists/${playlistId}/channels/new`);
            }}
          >
            Создать новый канал
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Подтвердите удаление</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить канал "{channelToDelete?.name}" из плейлиста?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Канал будет удален только из этого плейлиста, но останется доступным для добавления в другие плейлисты.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Отмена
          </Button>
          <Button 
            onClick={() => handleRemoveChannel(channelToDelete?.id)} 
            color="error" 
            variant="contained"
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Snackbar для предупреждения о drag-and-drop режиме */}
      <Snackbar
        open={showDragDropWarning}
        autoHideDuration={3000}
        onClose={() => setShowDragDropWarning(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowDragDropWarning(false)} 
          severity="warning" 
          sx={{ width: '100%' }}
        >
          Выключите режим перетаскивания для использования кнопок действий
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlaylistChannelsPage;
